import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  Search,
  BookOpen,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Zap,
  BarChart3,
  Users,
  Globe,
  Lightbulb,
} from "lucide-react";
import { useApi } from "../../hooks/useApi";
import toast from "react-hot-toast";
import "./Blog.css";
import PublicHeader from "../../components/PublicHeader/PublicHeader";
import ReadyToTransform from "../../components/ReadyToTransform/ReadyToTransform";
import Footer from "../../components/Footer/Footer";
import { getFreshS3Url } from "../../utils/s3Image";

const TEAL = "#00D1C1";
const CATEGORY_COLORS = {
  "AI & Automation": "#F43F5E",
  Productivity: "#366EB6",
  "Remote Work": "#115E59",
  "Team Management": "#6C59A6",
};

const categoryIcons = {
  "AI & Automation": Zap,
  Productivity: BarChart3,
  "Remote Work": Globe,
  "Team Management": Users,
};

const SEO_TITLE = "TeamGrid Blog | Workforce Analytics & Team Productivity Insights";
const SEO_DESCRIPTION = "Expert insights on AI-powered workforce analytics, team productivity, remote work best practices, and organizational intelligence from the TeamGrid team.";
const SEO_CANONICAL = "https://teamgrid.ai/blog";
const OG_IMAGE = "https://teamgrid.ai/og/og-home.webp";

const Blog = () => {
  const api = useApi();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("All Posts");
  const [blogs, setBlogs] = useState([]);
  const [featuredBlog, setFeaturedBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);

  const handleSubscribe = async () => {
    if (!email || !email.match(/^\S+@\S+\.\S+$/)) {
      return;
    }
    try {
      setSubscribing(true);
      const response = await api.post("/api/v1/newsletter/subscribe", { email });
      if (response.data.alreadySubscribed) {
        toast("This email is already subscribed!", { icon: "📧" });
      } else {
        setEmail("");
        setShowThankYou(true);
      }
    } catch (error) {
      console.error("Subscribe error:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubscribing(false);
    }
  };

  const filters = [
    "All Posts",
    "AI & Automation",
    "Productivity",
    "Remote Work",
    "Team Management",
  ];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  useEffect(() => {
    fetchBlogs();
  }, [selectedFilter, currentPage, searchQuery]);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const params = { page: currentPage, limit: 9 };
      if (selectedFilter !== "All Posts") params.category = selectedFilter;
      if (searchQuery) params.search = searchQuery;

      const response = await api.get("/api/v1/blogs", { params });
      const fetchedBlogs = response.data.data.blogs;

      const blogsWithFreshUrls = await Promise.all(
        fetchedBlogs.map(async (blog) => {
          if (blog.featuredImage) {
            const freshUrl = await getFreshS3Url(blog.featuredImage);
            return { ...blog, featuredImage: freshUrl || blog.featuredImage };
          }
          return blog;
        })
      );

      setBlogs(blogsWithFreshUrls);
      setTotalPages(response.data.data.pagination.pages);
      setFeaturedBlog(blogsWithFreshUrls.length > 0 ? blogsWithFreshUrls[0] : null);
    } catch (error) {
      console.error("Error fetching blogs:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const latestArticles = featuredBlog ? blogs.slice(1, 7) : blogs.slice(0, 6);

  const getInitials = (name) => {
    if (!name) return "TG";
    if (name.toLowerCase() === "teamgrid" || name.toLowerCase() === "team grid") return "TG";
    const words = name.trim().split(/\s+/);
    if (words.length >= 2) {
      return (words[0][0] + words[words.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <div className="blog-container bg-white min-h-screen" style={{ fontFamily: "Inter, sans-serif" }}>
      <Helmet>
        <title>{SEO_TITLE}</title>
        <meta name="description" content={SEO_DESCRIPTION} />
        <link rel="canonical" href={SEO_CANONICAL} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={SEO_CANONICAL} />
        <meta property="og:title" content={SEO_TITLE} />
        <meta property="og:description" content={SEO_DESCRIPTION} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:site_name" content="TeamGrid" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={SEO_CANONICAL} />
        <meta name="twitter:title" content={SEO_TITLE} />
        <meta name="twitter:description" content={SEO_DESCRIPTION} />
        <meta name="twitter:image" content={OG_IMAGE} />
      </Helmet>
      <PublicHeader />
      <div className="pt-16 sm:pt-18 md:pt-20">
        {/* Hero Section: same horizontal bounds as header */}
        <section
          className="flex flex-col items-center justify-center py-20"
          style={{
            background: "radial-gradient(70.71% 141.42% at 50% 0%, rgba(233, 252, 250, 0.6) 0%, rgba(233, 252, 250, 0) 50%), #FFFFFF",
          }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full flex flex-col items-center">
            <div className="flex flex-col items-center text-center w-full max-w-[768px] gap-5">
            <div
              className="inline-flex items-center gap-2 rounded-full border box-border"
              style={{
                padding: "4px 12px",
                background: "rgba(0, 209, 193, 0.06)",
                border: "1px solid rgba(0, 209, 193, 0.15)",
                borderRadius: 9999,
              }}
            >
              <BookOpen className="w-3 h-3 flex-shrink-0" style={{ color: "#00D1C1" }} />
              <span
                className="font-bold uppercase"
                style={{
                  fontFamily: "Inter",
                  fontSize: 12,
                  lineHeight: "16px",
                  letterSpacing: "0.6px",
                  color: "#00D1C1",
                }}
              >
                Insights & Best Practices
              </span>
            </div>
            <h1
              className="font-extrabold text-black text-center"
              style={{
                fontFamily: "Inter",
                fontSize: 62,
                lineHeight: "60px",
              }}
            >
              TeamGrid Blog
            </h1>
            <p
              className="text-center max-w-[705px]"
              style={{
                fontFamily: "Inter",
                fontWeight: 400,
                fontSize: 20,
                lineHeight: "28px",
                color: "rgba(0, 0, 0, 0.6)",
              }}
            >
              Expert insights on AI-powered workforce analytics, team productivity, and organizational intelligence.
            </p>
            </div>
          </div>
        </section>

        {/* Search & Filter: padding 32px, 1280px container, gap between search and categories */}
        <section
          className="bg-white border-b"
          style={{
            padding: "32px 0",
            boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
          }}
        >
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
            <div className="relative w-full sm:w-[384px] sm:max-w-[384px] flex-shrink-0">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
                style={{ color: "#A0A0A0" }}
              />
              <input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-[42px] pl-10 pr-4 bg-white border rounded-full text-black placeholder-[#A0A0A0] focus:outline-none focus:ring-2 focus:ring-[#00D1C1]/30 box-border"
                style={{
                  padding: "11px 16px 12px 40px",
                  border: "1px solid #EDF4FF",
                  borderRadius: 9999,
                  fontFamily: "Inter",
                  fontSize: 14,
                  lineHeight: "17px",
                }}
              />
            </div>
            <div className="flex items-center gap-2 overflow-x-auto overflow-y-hidden w-full sm:w-auto pb-2 sm:pb-0 hide-scrollbar" style={{ minHeight: 40 }}>
              {filters.map((filter) => (
                <button
                  key={filter}
                  type="button"
                  onClick={() => setSelectedFilter(filter)}
                  className="flex-shrink-0 rounded-full transition-all box-border"
                  style={
                    selectedFilter === filter
                      ? {
                          background: "#00D1C1",
                          color: "rgba(0, 0, 0, 0.6)",
                          padding: "6.5px 16px 7.5px",
                          fontFamily: "Inter",
                          fontWeight: 700,
                          fontSize: 12,
                          lineHeight: "16px",
                          borderRadius: 9999,
                        }
                      : {
                          border: "1px solid rgba(0, 0, 0, 0.1)",
                          color: "rgba(0, 0, 0, 0.6)",
                          padding: "6px 16px",
                          fontFamily: "Inter",
                          fontWeight: 500,
                          fontSize: 12,
                          lineHeight: "16px",
                          borderRadius: 9999,
                        }
                  }
                >
                  {filter}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Content: same horizontal bounds as header */}
        <section className="blog-content-wrapper">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <div className="blog-content-inner">
            {/* Featured Article: gap 20px, 4px teal bar, card 24px radius, content 48px padding */}
            {featuredBlog && (
              <div className="blog-featured-section">
                <div className="blog-section-head">
                  <div className="blog-bar blog-bar-teal" />
                  <h2 className="blog-section-title">Featured Article</h2>
                </div>
                <Link to={`/blog/${featuredBlog.slug}`} className="block w-full">
                  <div className="blog-featured-card">
                    <div className="blog-featured-image-area">
                      <div
                        className="absolute z-0"
                        style={{
                          left: 0,
                          right: 0,
                          top: 0,
                          bottom: 0,
                          // background: "linear-gradient(57.95deg, #CCFBF1 0%, #CCFBF1 0.27%, rgba(204, 251, 241, 0) 0.27%, rgba(204, 251, 241, 0) 1.63%), #FEF2F2",
                          opacity: 0.5,
                          flex: "none",
                          flexGrow: 0,
                          order: 0,
                        }}
                      />
                      {featuredBlog.featuredImage && featuredBlog.featuredImage.trim() !== "" ? (
                        <img
                          src={featuredBlog.featuredImage}
                          alt={featuredBlog.title}
                          className="relative z-10 w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      ) : (
                        <div className="blog-featured-placeholder">
                          {categoryIcons[featuredBlog.category] ? (
                            React.createElement(categoryIcons[featuredBlog.category], {
                              className: "w-12 h-12 text-black",
                              style: { transform: "rotate(-12deg)" },
                            })
                          ) : (
                            <Lightbulb className="w-12 h-12 text-black" style={{ transform: "rotate(-12deg)" }} />
                          )}
                        </div>
                      )}
                    </div>
                    <div className="blog-featured-content">
                      <div className="flex flex-col gap-4">
                        <div className="flex items-center gap-3 flex-wrap" style={{ gap: 12 }}>
                          <span
                            className="py-1 px-2 rounded text-xs font-bold"
                            style={{ background: "rgba(0,0,0,0.05)", color: CATEGORY_COLORS[featuredBlog.category] || "#F43F5E", padding: "4px 8px", borderRadius: 4 }}
                          >
                          {featuredBlog.category}
                        </span>
                        {/* <span className="text-[#475569] text-xs">•</span> */}
                        {/* <span className="text-[#475569] text-xs">{featuredBlog.readTime || "8 min read"}</span> */}
                        <span className="text-[#475569] text-xs">•</span>
                        <span className="text-[#475569] text-xs">{formatDate(featuredBlog.publishedAt)}</span>
                      </div>
                      <h3 className="font-extrabold text-black mb-4" style={{ fontFamily: "Inter", fontSize: 30, lineHeight: "36px" }}>
                        {featuredBlog.title}
                      </h3>
                      <p className="text-[#475569] mb-8" style={{ fontFamily: "Inter", fontSize: 16, lineHeight: "26px" }}>
                        {featuredBlog.description}
                      </p>
                      <div className="flex items-center justify-between flex-wrap gap-4">
                        <div className="flex items-center gap-3">
                          <div
                            className="w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold"
                            style={{ background: "#000000", color: "#FFFFFF" }}
                          >
                            {getInitials(featuredBlog.author)}
                          </div>
                          <div>
                            <div className="font-bold text-black text-sm">{featuredBlog.author}</div>
                            <div className="text-[#475569] text-xs">Head of Data</div>
                          </div>
                        </div>
                        <span className="font-bold flex items-center gap-1 text-[#00D1C1]" style={{ fontSize: 14, lineHeight: "20px" }}>
                          Read Article
                          <ArrowRight className="w-4 h-4" />
                        </span>
                      </div>
                    </div>
                    </div>
                  </div>
                </Link>
              </div>
            )}

            {/* Latest Articles: 4px gray bar, gap 32px, cards 16px radius, border #EDF4FF */}
            <div className="blog-latest-section">
              <div className="blog-section-head">
                <div className="blog-bar blog-bar-gray" />
                <h2 className="blog-section-title">Latest Articles</h2>
              </div>

              {loading ? (
                <div className="flex justify-center py-16">
                  <div className="w-12 h-12 border-2 border-[#00D1C1] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : latestArticles.length === 0 ? (
                <p className="text-[#475569] text-center py-12">No articles found.</p>
              ) : (
                <div className="blog-latest-grid">
                  {latestArticles.map((article, idx) => {
                    const IconComponent = categoryIcons[article.category] || Lightbulb;
                    const catColor = CATEGORY_COLORS[article.category] || "#366EB6";
                    return (
                      <Link key={article._id || idx} to={`/blog/${article.slug}`} className="block">
                        <div
                          className="rounded-[16px] border overflow-hidden bg-white h-full flex flex-col transition-shadow hover:shadow-lg box-border"
                          style={{ borderColor: "#EDF4FF" }}
                        >
                          <div
                            className="h-[192px] flex items-center justify-center relative"
                            style={{
                              background: "radial-gradient(70.71% 70.71% at 50% 50%, #CBD5E1 2.95%, rgba(203, 213, 225, 0) 2.95%), #EDF4FF",
                            }}
                          >
                            {article.featuredImage && article.featuredImage.trim() !== "" ? (
                              <img
                                src={article.featuredImage}
                                alt={article.title}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.style.display = "none";
                                  if (e.target.nextSibling) e.target.nextSibling.style.display = "flex";
                                }}
                              />
                            ) : null}
                            <div
                              className="w-14 h-14 rounded-[12px] bg-white flex items-center justify-center shadow-sm absolute"
                              style={{
                                boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
                                display: article.featuredImage && article.featuredImage.trim() !== "" ? "none" : "flex",
                              }}
                            >
                              <IconComponent className="w-6 h-6" style={{ color: catColor }} />
                            </div>
                          </div>
                          <div className="blog-article-card-content">
                            <div>
                              <span
                                className="inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase mb-3 w-fit leading-[15px]"
                                style={{ background: "rgba(0,0,0,0.05)", color: catColor }}
                              >
                                {article.category}
                              </span>
                              <h3 className="font-bold text-black mb-3 line-clamp-2" style={{ fontFamily: "Inter", fontSize: 18, lineHeight: "28px" }}>
                                {article.title}
                              </h3>
                              <p className="text-[#475569] text-sm mb-4 line-clamp-3 flex-1" style={{ fontFamily: "Inter", fontSize: 14, lineHeight: "20px" }}>
                                {article.description}
                              </p>
                            </div>
                            <div className="pt-4 border-t border-white flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <div
                                  className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold"
                                  style={{ background: "#000000", color: "#FFFFFF" }}
                                >
                                  {getInitials(article.author)}
                                </div>
                                <span className="text-[#475569] text-xs font-medium" style={{ lineHeight: "16px" }}>{article.author}</span>
                              </div>
                              {/* <span className="text-[#A0A0A0] text-xs" style={{ fontSize: 12, lineHeight: "16px" }}>{article.readTime || "5 min"}</span> */}
                            </div>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Newsletter: 896px max, padding 24px 40px, gap 12px, input/button 8px radius */}
            <div className="blog-newsletter">
              <h3 className="blog-newsletter-title">Stay Ahead with Weekly Insights</h3>
              <p className="blog-newsletter-desc">
                Get expert tips on workforce analytics, productivity strategies, and AI automation delivered to your inbox every week.
              </p>
              <div className="blog-newsletter-form">
                <input
                  type="email"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="blog-newsletter-input"
                />
                <button
                  type="button"
                  className="blog-newsletter-btn"
                  onClick={handleSubscribe}
                  disabled={subscribing}
                  style={{ opacity: subscribing ? 0.7 : 1 }}
                >
                  {subscribing ? "Subscribing..." : "Subscribe"}
                </button>
              </div>
            </div>

            {/* Thank You Popup */}
            {showThankYou && (
              <div
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(0,0,0,0.5)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 9999,
                }}
                onClick={() => setShowThankYou(false)}
              >
                <div
                  style={{
                    background: "#fff",
                    borderRadius: 16,
                    padding: "48px 40px",
                    maxWidth: 440,
                    width: "90%",
                    textAlign: "center",
                    boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div
                    style={{
                      width: 64,
                      height: 64,
                      borderRadius: "50%",
                      background: "#00D1C1",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 20px",
                    }}
                  >
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                  <h3 style={{ fontSize: 24, fontWeight: 700, color: "#1a1a2e", margin: "0 0 8px" }}>
                    Thank You!
                  </h3>
                  <p style={{ fontSize: 16, color: "#64748b", margin: "0 0 24px", lineHeight: 1.5 }}>
                    You've successfully subscribed to our weekly insights.
                  </p>
                  <button
                    onClick={() => setShowThankYou(false)}
                    style={{
                      background: "#00D1C1",
                      color: "#fff",
                      border: "none",
                      borderRadius: 8,
                      padding: "12px 32px",
                      fontSize: 16,
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    Got it!
                  </button>
                </div>
              </div>
            )}

            {/* Pagination: below newsletter, arrows + numbered pills, active teal */}
            {totalPages >= 1 && (
              <div className="blog-pagination-wrapper">
                <div className="blog-pagination">
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="blog-pagination-arrow"
                    aria-label="Previous page"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  {(() => {
                    const maxVisible = 5;
                    const start = Math.max(1, Math.min(currentPage - 2, totalPages - maxVisible + 1));
                    const end = Math.min(totalPages, start + maxVisible - 1);
                    const pages = [];
                    for (let p = start; p <= end; p++) pages.push(p);
                    return pages.map((pageNum) => (
                      <button
                        key={pageNum}
                        type="button"
                        onClick={() => setCurrentPage(pageNum)}
                        className={"blog-pagination-num" + (pageNum === currentPage ? " blog-pagination-num-active" : "")}
                      >
                        {pageNum}
                      </button>
                    ));
                  })()}
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="blog-pagination-arrow"
                    aria-label="Next page"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
            </div>
          </div>
        </section>

        <ReadyToTransform />
        <Footer />
      </div>
    </div>
  );
};

export default Blog;
